import { createContext, ReactNode, useContext, useRef } from 'react';

export const InputContext = createContext({ size: 'regular', variant: 'border', disabled: false });

export type InputProps = {
    inputRef?: React.RefObject<HTMLInputElement>;

    /**
     * Render the input with a red border.
     */
    isError?: boolean;
    /**
     * An error message to show in red under the input.
     */
    errorMessage?: string;
    /**
     * Helpful message to show under the input. If an errorMessage is provided, this will be hidden.
     */
    hint?: string;
    /**
     * Small title to give context about the input e.g. Email
     */
    label?: string;

    /**
     * Label to signal that this input is not reauired.
     */
    isOptional?: boolean;
    /**
     * Limits what data can the user input.
     */
    type: 'text' | 'date';

    /**
     * Input's value
     */
    value?: string;
    /**
     * Function to validate the current value. The onChange event will only fire if validation passes.
     */
    validator?: (value: string) => boolean;
    /**
     * How to align the value and placeholder.
     */
    textAlign?: 'left' | 'right' | 'center';
    /**
     * Shows the input with a surrounding border or just an underline.
     */
    variant?: 'border' | 'underline';

    /**
     * Input box size.
     */
    size?: 'small' | 'regular';

    /**
     * Text to display at the end of input
     */
    trailingText?: string;

    children?: ReactNode;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'value'>;

function Input({
    value,
    variant = 'border',
    isError,
    errorMessage = '',
    hint,
    label,
    isOptional,
    validator,
    textAlign = 'left',
    size = 'regular',
    trailingText,
    children,
    inputRef,
    ...props
}: InputProps): JSX.Element {
    const ref = useRef<HTMLInputElement | null>(null);

    const borderStyle =
        variant === 'border'
            ? `border rounded-md ${isError ? 'focus:border-red-500 focus:ring-0' : 'focus:border-indigo-500'}`
            : `border-0 border-b p-0 m-0 ${isError ? 'focus:border-b-red-500' : 'focus:border-b-indigo-500'} focus:ring-0`;

    const sizeStyles =
        size === 'regular'
            ? `text-sm ${variant === 'border' ? 'bottom-[9px]' : 'bottom-[1px] pb-2 focus:border-b-2'}`
            : `text-xs ${variant === 'border' ? 'bottom-[9px]' : 'bottom-[1px] pb-1 focus:border-b-2'}`;

    function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        if (!props.onChange) {
            return;
        }
        if (!e.target.value) {
            props.onChange(e);
        }
        if (!validator) {
            props.onChange(e);
        } else {
            if (validator(e.target.value)) {
                props.onChange(e);
            }
        }
    }

    return (
        <InputContext.Provider value={{ disabled: Boolean(props.disabled), size, variant }}>
            <div>
                {/* LABEL + OPTIONAL */}
                {(label || isOptional) && (
                    <div className="flex justify-between">
                        {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
                        {isOptional && <span className="text-sm text-gray-500 mb-1">Optional</span>}
                    </div>
                )}
                {/* INPUT */}
                <div className="relative">
                    {/* Dropdown */}
                    {children}
                    <input
                        {...props}
                        ref={inputRef || ref}
                        value={value}
                        className={`${borderStyle} ${isError ? 'border-red-300' : 'border-gray-300'} ${sizeStyles} ${
                            children ? 'pl-16' : ''
                        } ${
                            trailingText ? 'pr-10' : ''
                        } block w-full box-border disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500`}
                        onChange={handleChange}
                        style={{ textAlign }}
                    />
                    {trailingText && (
                        <div className={`pointer-events-none absolute right-0 flex items-center pr-3 ${sizeStyles}`}>
                            <span className="text-gray-500">{trailingText}</span>
                        </div>
                    )}
                </div>

                {/* HINT */}
                {hint && !errorMessage && <p className="mt-2 text-sm text-gray-500">{hint}</p>}

                {/* ERROR MESSAGE */}
                {errorMessage && <p className="mt-2 text-sm text-red-500">{errorMessage}</p>}
            </div>
        </InputContext.Provider>
    );
}

type DropdownProps = {
    options: { label: string; value: string }[];
    value?: string;
    name: string;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

function Dropdown({ options, name, value, ...props }: DropdownProps): JSX.Element {
    const { disabled, size, variant } = useContext(InputContext);

    const sizeStyles = size === 'regular' ? 'text-sm' : 'text-xs';
    const borderStyles = variant === 'border' ? '' : 'mb-2';

    return (
        <div className="absolute inset-y-0 left-0 flex items-center">
            <label htmlFor={name} className="sr-only">
                Country
            </label>
            <select
                disabled={disabled}
                id={name}
                name={name}
                value={value}
                autoComplete={name}
                className={`h-full rounded-md border-transparent bg-transparent py-0 pr-7 text-gray-500 focus:border-indigo-500 focus:ring-indigo-500 ${sizeStyles} ${borderStyles}`}
                {...props}
            >
                {options.map(({ value, label }) => {
                    return (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    );
                })}
            </select>
        </div>
    );
}

Input.Dropdown = Dropdown;

export default Input;

// onBlur={(e): void => {
//     if (ref.current && value) {
//         ref.current.value = Intl.NumberFormat(undefined, {
//             minimumFractionDigits: 2,
//             maximumFractionDigits: 2,
//         }).format(+(value === '.' ? '0' : value));
//     }
// }}
// onFocus={(): void => {
//     if (ref.current) {
//         ref.current.value = value;
//     }
// }}
